import React from 'react';

import css from '../articleProse.module.css';

// Body for /blog/understanding-listing-analytics-on-avento.
// Copy is taken verbatim from the source article.
const UnderstandingListingAnalytics = () => (
  <div className={css.prose}>
    <p className={css.lead}>
      When you publish your skydiving gear on Avento, we start collecting data on how people
      interact with your listing: how many times it appears in search, how many people click on it,
      and how many save it to their favorites. You can find all of this under{' '}
      <a href="/listings" className={css.link}>
        Profile &gt; Your Listings &gt; Insights
      </a>
      .
    </p>
    <p className={css.lead}>
      It's tempting to just watch the numbers go up. But what do they actually tell you? Is 200 views
      good? Are 4 saves a lot? And what should you do differently when the numbers feel low?
    </p>
    <p className={css.lead}>
      This article breaks down what each metric means, how it's calculated, and what it tells you
      about where your listing stands.
    </p>

    <h2>What Each Metric Means</h2>
    <p>
      We started collecting data in early June 2026. If your listing was already online before then,
      the total numbers shown are estimated based on your listing's performance since we started
      tracking. The real numbers are likely a bit higher, since most listings get the most views
      right after they go live.
    </p>

    <h3>Days Online</h3>
    <p>
      The number of days your listing has been available for purchase on Avento. Days when your
      listing was closed do not count, since buyers could not have purchased it during that time.
    </p>

    <h3>Saves to Favorites</h3>
    <p>
      The number of buyers who have currently saved your listing to their favorites. This is a strong
      signal: a buyer looked at your listing, found it interesting, and saved it to come back later.
      Not everyone who saves will buy, but almost everyone who buys has saved first.
    </p>

    <h3>Views</h3>
    <p>
      How many times your listing was opened. This includes buyers browsing Avento directly, but also
      clicks from external sources like Google Search, Google Shopping, or a shared link.
    </p>

    <h3>Impressions</h3>
    <p>
      How many times your listing card appeared in an Avento search. Every time a buyer scrolls
      through results and your listing is visible on screen, that counts as one impression. Scrolling
      past the same listing multiple times only counts once, and only if your listing was on screen
      for at least one second.
    </p>

    <h3>Click Rate</h3>
    <p>
      How often buyers who saw your listing card in a search actually clicked on it. It tells you how
      attractive your listing looks at first glance: your cover photo, title, and price. Click rate
      only appears once your listing has at least 100 impressions, since anything below that does not
      give you enough data to draw conclusions. If you change your price, the click rate resets,
      because price has a significant effect on how appealing your listing looks in search results.
    </p>

    <h3>Performance Rating</h3>
    <p>
      Your click rate compared to similar listings on Avento, meaning listings in the same category.
      It tells you how your listing card stacks up against the competition. If your performance rating
      is in the bottom half, something fundamental usually needs to change for your listing to sell.
    </p>
    <p>To improve your performance rating:</p>
    <ul>
      <li>Lower the price if it is above market value</li>
      <li>Upload better photos and pick a strong cover image</li>
      <li>Write a clear, specific listing title</li>
      <li>
        Fill out as many structured fields as possible so your listing appears when buyers filter for
        exactly what you are selling
      </li>
    </ul>
    <p>
      A good performance rating is a strong signal, but it does not guarantee sales on its own. If
      your rating is strong but you are still not receiving saves, offers, or inquiries, something
      else is holding buyers back, usually the price relative to what they see inside the listing.
    </p>

    <h2>Stages of a Sale</h2>
    <p>
      Here's where the data becomes genuinely useful. Each metric measures a different moment in the
      buyer's journey. Thinking in stages helps you identify exactly where your listing is losing
      people.
    </p>

    <h3>Stage 1: Getting Found</h3>
    <p>
      Impressions, views, click rate, and performance rating all belong here. A buyer is browsing,
      exploring options across different channels and categories.
    </p>
    <p>
      If your impressions and views are low, it means either buyers are not searching for what you are
      selling, or your listing is missing enough information for the search to surface it.
    </p>
    <p>
      <strong>What to do:</strong> Fill out every field you can. The more structured data your listing
      has, the more precisely it matches what buyers are actually searching for.
    </p>

    <h3>Stage 2: Deciding to Buy</h3>
    <p>
      Saves to favorites, inquiries, and offers belong here. At this stage, a buyer is genuinely
      interested and evaluating whether to go through with it.
    </p>
    <p>
      If your listing is getting good impressions and click rates but almost no saves or inquiries,
      buyers are clicking through and leaving. They see your listing, but something is not connecting.
      Most often: the price feels off relative to what they see inside.
    </p>
    <p>
      <strong>What to do:</strong> If you are getting traffic but no engagement, revisit the price.
      Compare it honestly to similar listings currently on Avento. You can also use our{' '}
      <a href="/gear-value-calculator" className={css.link}>
        Gear Value Calculator
      </a>{' '}
      to get an estimate.
    </p>

    <h3>Stage 3: Completing the Purchase</h3>
    <p>
      An accepted offer or a direct purchase. If you got here, everything worked. Well done.
    </p>

    <h2>Low Engagement Across the Board</h2>
    <p>
      If your listing is not getting found in searches, not being clicked when it does appear, and not
      being saved, the most common cause is pricing. Buyers often filter by price range before
      browsing, which means an overpriced listing gets excluded before it is ever seen. Avento's
      search ranking also factors in listing attractiveness, and a listing with no engagement signals
      low interest, which reduces how often it appears over time.
    </p>
    <p>
      A good rule of thumb: set a fair price from the start rather than listing high and adding
      "willing to negotiate" in the description. Listings priced fairly sell faster and usually closer
      to the asking price than ones that start high and get ignored.
    </p>

    <h2>How to Use This to Sell Better</h2>
    <p>Now that you understand what each number means, here is how to act on it.</p>
    <p>
      <strong>If impressions are low:</strong> Your listing is not being found. The search is not
      connecting your listing to what buyers are typing. Go back and fill out everything: category,
      brand, model, size, year, number of jumps, condition. Every field you skip is a filter you might
      be excluded from.
    </p>
    <p>
      <strong>If click rate is low (or performance rating is in the bottom half):</strong> Buyers see
      your listing but skip it. The cover photo, title, or price is not convincing enough at a glance.
      Try a better main photo, taken in good light with a clean background. Rewrite the title to be
      specific, for example "PD Sabre 3, 150 sqft., 2024" instead of something vague like "Great main
      canopy!".
    </p>
    <p>
      <strong>If you get good traffic but no saves or inquiries:</strong> The listing is getting
      attention but not converting. Buyers are clicking through and deciding against it. This usually
      means the price feels high for what they are seeing, or the description does not build enough
      trust. Add more photos from different angles, describe the gear honestly and in detail, and
      mention anything a buyer would want to know before asking. And most importantly, be honest to
      yourself if the price you set is fair and attractive.
    </p>
    <p>
      <strong>If everything looks fine but the listing is just sitting there:</strong> Some gear takes
      longer to sell because the buyer pool is smaller. Very small or large sizes, especially specific
      models, or very high-performance gear often has a smaller but more targeted audience. Be
      patient, keep the listing up to date, and share the link in relevant channels: your DZ WhatsApp
      group, Facebook buy &amp; sell groups, or anywhere else your gear's target buyer might see it.
    </p>

    <p className={css.signoff}>Cheers!</p>
  </div>
);

export default UnderstandingListingAnalytics;
